import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Star, Truck, Shield, RefreshCw, Minus, Plus, Heart, Share2, Check, Store, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { fetchProduct, fetchCategories } from '../lib/api';
import { reviews } from '../lib/constants';
import ProductCard from '../components/ProductCard';

const Product = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toast } = useToast();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [categories, setCategories] = useState([]);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setNotFound(false);
    setActiveImg(0);
    setQty(1);
    fetchProduct(productId)
      .then((d) => { setProduct(d.product); setRelated(d.related); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [productId]);

  if (loading) {
    return <div className="flex items-center justify-center py-32 text-[#00847A]"><Loader2 size={40} className="animate-spin" /></div>;
  }

  if (notFound || !product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-black text-[#262626] mb-4">Produit introuvable</h1>
        <Button onClick={() => navigate('/')} className="bg-[#00847A] hover:bg-[#006B63] text-white rounded-full h-11 px-6">Retour à l'accueil</Button>
      </div>
    );
  }

  const category = categories.find((c) => c.id === product.category);

  const handleAdd = () => {
    addToCart(product, qty);
    toast({ title: 'Produit ajouté au panier', description: `${qty} × ${product.name}` });
  };

  const handleBuyNow = () => {
    addToCart(product, qty);
    navigate('/panier');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="product-page">
      <nav className="flex items-center gap-1 text-sm text-[#595959] mb-4">
        <Link to="/" className="hover:text-[#00847A]">Accueil</Link>
        <ChevronRight size={14} />
        <Link to={`/categorie/${category?.id}`} className="hover:text-[#00847A]">{category?.name}</Link>
        <ChevronRight size={14} />
        <span className="text-[#262626] line-clamp-1">{product.name}</span>
      </nav>

      <div className="grid md:grid-cols-[1fr_1fr_320px] gap-6">
        {/* Gallery */}
        <div>
          <div className="aspect-square bg-neutral-50 rounded-xl overflow-hidden border border-neutral-200">
            <img src={product.images[activeImg] || product.image} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {product.images.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square rounded-lg overflow-hidden border-2 ${i === activeImg ? 'border-[#00847A]' : 'border-neutral-200'}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="text-sm text-[#00847A] font-semibold mb-1">{product.brand}</div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#262626] leading-tight" data-testid="product-name">{product.name}</h1>

          <div className="flex items-center gap-3 mt-3">
            <div className="flex items-center">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={16} className={i <= Math.round(product.rating) ? 'fill-[#FFB800] text-[#FFB800]' : 'text-neutral-300'} />
              ))}
            </div>
            <span className="text-sm font-semibold text-[#262626]">{product.rating}/5</span>
            <span className="text-sm text-[#595959]">({product.reviews.toLocaleString('fr-FR')} avis)</span>
          </div>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="text-4xl font-black text-[#262626]">{product.price.toFixed(2).replace('.', ',')} €</span>
            {product.oldPrice > product.price && (
              <>
                <span className="text-lg text-[#595959] line-through">{product.oldPrice.toFixed(2).replace('.', ',')} €</span>
                <span className="bg-[#FF4D4D] text-white text-sm font-bold px-2 py-1 rounded">-{product.discount}%</span>
              </>
            )}
          </div>
          <div className="text-xs text-[#595959] mt-1">Économisez {(product.oldPrice - product.price).toFixed(2).replace('.', ',')} €</div>

          <div className="mt-5 p-3 rounded-lg bg-[#F0F9F8] border border-[#00847A]/20 flex items-center gap-2 text-sm text-[#00847A]">
            <Store size={18} /> Vendu par <span className="font-semibold">{product.seller}</span>
          </div>

          <div className="mt-6">
            <h3 className="font-bold text-[#262626] mb-2">Caractéristiques</h3>
            <ul className="space-y-1.5">
              {product.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#262626]">
                  <Check size={16} className="text-[#00847A] mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-6 text-[#595959] text-sm leading-relaxed">{product.description}</p>
        </div>

        {/* Buy box */}
        <aside className="bg-white border border-neutral-200 rounded-xl p-5 h-fit sticky top-32">
          <div className="text-3xl font-black text-[#262626]">{product.price.toFixed(2).replace('.', ',')} €</div>
          <div className="flex items-center gap-2 text-sm text-[#00847A] mt-2">
            <Truck size={16} /> {product.shipping}
          </div>
          <div className="text-sm text-[#262626] mt-1">Livraison {product.delivery.toLowerCase()}</div>
          {product.stock > 0 ? (
            <div className="flex items-center gap-2 text-sm text-green-600 mt-2">
              <Check size={16} /> En stock{product.stock <= 10 ? ` · plus que ${product.stock} !` : ''}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-[#FF4D4D] mt-2 font-semibold" data-testid="out-of-stock">
              Rupture de stock
            </div>
          )}

          <div className="flex items-center gap-3 mt-5">
            <span className="text-sm text-[#262626]">Quantité</span>
            <div className="flex items-center border border-neutral-300 rounded-full">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100 rounded-full"><Minus size={14} /></button>
              <span className="w-8 text-center text-sm font-semibold" data-testid="product-qty">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100 rounded-full"><Plus size={14} /></button>
            </div>
          </div>

          <Button onClick={handleAdd} disabled={product.stock <= 0} data-testid="add-to-cart-btn" className="w-full mt-4 bg-[#00847A] hover:bg-[#006B63] text-white rounded-full h-11 font-semibold disabled:opacity-50">
            {product.stock > 0 ? 'Ajouter au panier' : 'Indisponible'}
          </Button>
          <Button onClick={handleBuyNow} disabled={product.stock <= 0} data-testid="buy-now-btn" variant="outline" className="w-full mt-2 border-[#FF4D4D] text-[#FF4D4D] hover:bg-[#FF4D4D] hover:text-white rounded-full h-11 font-semibold disabled:opacity-50">
            Acheter maintenant
          </Button>

          <div className="flex gap-2 mt-3">
            <button className="flex-1 flex items-center justify-center gap-1 text-sm text-[#595959] hover:text-[#00847A] py-2"><Heart size={16} /> Favoris</button>
            <button className="flex-1 flex items-center justify-center gap-1 text-sm text-[#595959] hover:text-[#00847A] py-2"><Share2 size={16} /> Partager</button>
          </div>

          <div className="mt-5 pt-5 border-t border-neutral-200 space-y-3 text-sm">
            <div className="flex items-start gap-2 text-[#262626]">
              <Shield className="text-[#00847A] mt-0.5 shrink-0" size={18} />
              <div>Paiement sécurisé</div>
            </div>
            <div className="flex items-start gap-2 text-[#262626]">
              <RefreshCw className="text-[#00847A] mt-0.5 shrink-0" size={18} />
              <div>Retour gratuit 30 jours</div>
            </div>
            <div className="flex items-start gap-2 text-[#262626]">
              <Truck className="text-[#00847A] mt-0.5 shrink-0" size={18} />
              <div>Livraison offerte dès 50€</div>
            </div>
          </div>
        </aside>
      </div>

      {/* Tabs */}
      <div className="mt-10">
        <Tabs defaultValue="desc">
          <TabsList className="bg-neutral-100">
            <TabsTrigger value="desc" className="data-[state=active]:bg-white data-[state=active]:text-[#00847A]">Description</TabsTrigger>
            <TabsTrigger value="specs" className="data-[state=active]:bg-white data-[state=active]:text-[#00847A]">Caractéristiques</TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-white data-[state=active]:text-[#00847A]">Avis ({product.reviews})</TabsTrigger>
          </TabsList>
          <TabsContent value="desc" className="pt-6">
            <p className="text-[#262626] leading-relaxed">{product.description}</p>
            <p className="text-[#595959] mt-4 leading-relaxed">Ce produit est idéal pour les professionnels et les bricoleurs exigeants. Fabriqué avec des matériaux de qualité, il garantit robustesse et performance sur le long terme. L'ergonomie a été étudiée pour un confort d'utilisation optimal.</p>
          </TabsContent>
          <TabsContent value="specs" className="pt-6">
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-neutral-200"><td className="py-3 font-semibold text-[#262626] w-1/3">Marque</td><td className="py-3 text-[#595959]">{product.brand}</td></tr>
                <tr className="border-b border-neutral-200"><td className="py-3 font-semibold text-[#262626]">Catégorie</td><td className="py-3 text-[#595959]">{category?.name}</td></tr>
                {product.features.map((f, i) => (
                  <tr key={i} className="border-b border-neutral-200"><td className="py-3 font-semibold text-[#262626]">Caractéristique {i + 1}</td><td className="py-3 text-[#595959]">{f}</td></tr>
                ))}
              </tbody>
            </table>
          </TabsContent>
          <TabsContent value="reviews" className="pt-6">
            <div className="space-y-4">
              {reviews.map((r) => (
                <div key={r.id} className="border-b border-neutral-200 pb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-[#262626]">{r.name}</span>
                    {r.verified && <span className="text-xs text-[#00847A] bg-[#F0F9F8] px-2 py-0.5 rounded-full">Achat vérifié</span>}
                    <span className="text-xs text-[#595959] ml-auto">{r.date}</span>
                  </div>
                  <div className="flex items-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} className={i <= r.rating ? 'fill-[#FFB800] text-[#FFB800]' : 'text-neutral-300'} />)}
                  </div>
                  <p className="text-sm text-[#262626]">{r.comment}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Related products */}
      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="text-2xl font-black text-[#262626] mb-6">Produits similaires</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
};

export default Product;
